/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {nullify} from '@dvrd/dvr-controls';

import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';

import {ActivateHandle, NullString, UserFilter} from '../../../../util/interfaces';

import UsersFilter from './usersFilter';

interface Props {
    onSubmit: (filter: UserFilter) => void;
}

export const emptyFilter: UserFilter = {
    user_id: null,
    email: null,
    firstname: null,
    account_id: null,
    company_name: null,
    lastname: null,
    customer_id: null
};

export function isFilterActive(filter: UserFilter): boolean {
    return Object.values(filter).some(value => value !== null && value !== '');
}

function UsersFilterController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onSubmit} = props;
    const [active, setActive] = useState(false);
    const [filter, setFilter] = useState<UserFilter>(emptyFilter);

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setFilter(emptyFilter);
    }

    function onChangeFilter(key: keyof UserFilter) {
        return function (value: NullString) {
            value = nullify(value);
            setFilter(Object.assign({}, filter, {[key]: value}));
        };
    }

    function _onSubmit() {
        onSubmit(filter);
        onClose();
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <UsersFilter
            onChangeFilter={onChangeFilter}
            onSubmit={_onSubmit}
            onClose={onClose}
            filter={filter}
            active={active}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(UsersFilterController);
